import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MobileLayout from '../../components/MobileLayout';
import {
  WriterProfileData,
  emptyWriterProfile,
  fromResponse,
} from '../../features/writerProfile/types';
import { getWriterProfile, saveWriterProfile } from '../../features/writerProfile/api';
import {
  FormatsSection,
  GenresSection,
  InfluencesSection,
  StyleSection,
  GoalSection,
} from '../../features/writerProfile/components';

const WriterProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<WriterProfileData>(emptyWriterProfile());
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getWriterProfile()
      .then((profile) => {
        if (!cancelled) setData(fromResponse(profile));
      })
      .catch(() => {
        // Never show an empty form we could then save over a real profile.
        if (!cancelled) {
          setLoadFailed(true);
          toast.error(t('writerProfile.loadError', 'Could not load your writer profile'));
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [t]);

  const patch = (p: Partial<WriterProfileData>) => setData((d) => ({ ...d, ...p }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveWriterProfile(data);
      toast.success(t('writerProfile.saved', 'Writer profile saved'));
      navigate('/profile');
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          t('writerProfile.saveError', 'Failed to save writer profile'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              aria-label={t('common.back', 'Back')}
              className="w-10 h-10 flex items-center justify-center rounded-full"
            >
              <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--ds-text-primary)' }} />
            </button>
            <h1 className="ml-4 text-xl font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
              {t('writerProfile.title', 'Writer profile')}
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
            />
          </div>
        ) : loadFailed ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <p className="mb-4" style={{ color: 'var(--ds-text-secondary)' }}>
              {t('writerProfile.loadError', 'Could not load your writer profile')}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold"
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSave} className="flex-1 px-6 py-6 space-y-8">
            <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'writerProfile.intro',
                'The AI reads this before it writes with you. Update it whenever your taste changes.',
              )}
            </p>
            <FormatsSection data={data} onChange={patch} />
            <GenresSection data={data} onChange={patch} />
            <InfluencesSection data={data} onChange={patch} />
            <StyleSection data={data} onChange={patch} />
            <GoalSection data={data} onChange={patch} />
            <button
              type="submit"
              disabled={saving}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors"
            >
              {saving ? t('writerProfile.saving', 'Saving…') : t('writerProfile.save', 'Save')}
            </button>
          </form>
        )}
      </div>
    </MobileLayout>
  );
};

export default WriterProfile;
